import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import useSectionScroll from "../../hooks/useSectionScroll";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const goToSection = useSectionScroll();

  /*
   * Detect page scroll
   */
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  /*
   * Navbar links
   */
  const links = [
    {
      label: "Home",
      type: "route",
      path: "/",
    },
    {
      label: "Services",
      type: "route",
      path: "/services",
    },
    {
      label: "Technology",
      type: "scroll",
      id: "technology",
    },
    {
      label: "About",
      type: "route",
      path: "/about",
    },
    {
      label: "FAQ",
      type: "scroll",
      id: "faq",
    },
  ];

  /*
   * Handle section scrolling
   */
  const handleScroll = (id) => {
    setMenuOpen(false);
    goToSection(id);
  };

  /*
   * Disable body scrolling when mobile menu is open
   */
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 z-[9999] h-20 w-full transition-all duration-500 ${
          menuOpen
            ? "bg-white border-b border-stone-200 shadow-sm"
            : scrolled
            ? "bg-white/85 backdrop-blur-xl border-b border-stone-200/60 shadow-sm"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-full max-w-7xl items-center justify-between px-6 lg:px-12">
          {/* Logo */}
          <Link
            to="/"
            onClick={() => setMenuOpen(false)}
            className="flex shrink-0 items-center gap-3"
          >
            <img
              src="/logo1.png"
              alt="Logo"
              className="h-[120px] w-[150px] object-contain"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden items-center gap-7 lg:flex">
            {links.map((link) =>
              link.type === "route" ? (
                <Link
                  key={link.label}
                  to={link.path}
                  className="text-[11px] font-semibold uppercase tracking-widest text-[#1A1714] transition-colors duration-300 hover:text-[#C8762A]"
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.label}
                  type="button"
                  onClick={() => handleScroll(link.id)}
                  className="text-[11px] font-semibold uppercase tracking-widest text-[#1A1714] transition-colors duration-300 hover:text-[#C8762A]"
                >
                  {link.label}
                </button>
              )
            )}
          </div>

          {/* Desktop Contact Button */}
          <div className="hidden lg:flex">
            <Link
              to="/contact"
              className="group relative inline-flex items-center justify-center overflow-hidden rounded-full border border-[#1A1714] px-6 py-2.5 text-sm font-semibold text-[#1A1714]"
            >
              <span className="absolute inset-0 bg-[#1A1714] transition-all duration-300 ease-out [clip-path:circle(0%_at_50%_50%)] group-hover:[clip-path:circle(100%_at_50%_50%)]" />

              <span className="relative transition group-hover:text-white">
                Book Consultation
              </span>
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            className="relative z-[10001] flex flex-col gap-1.5 p-2 lg:hidden"
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            <span
              className={`h-0.5 w-6 bg-black transition-all duration-300 ${
                menuOpen ? "translate-y-2 rotate-45" : ""
              }`}
            />

            <span
              className={`h-0.5 w-6 bg-black transition-all duration-300 ${
                menuOpen ? "opacity-0" : ""
              }`}
            />

            <span
              className={`h-0.5 w-6 bg-black transition-all duration-300 ${
                menuOpen ? "-translate-y-2 -rotate-45" : ""
              }`}
            />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-x-0 bottom-0 top-20 z-[9998] bg-white transition-all duration-300 lg:hidden ${
          menuOpen
            ? "visible translate-y-0 opacity-100"
            : "invisible -translate-y-3 opacity-0 pointer-events-none"
        }`}
      >
        <div className="h-full overflow-y-auto px-8 py-10">
          <div className="flex flex-col gap-6">
            {links.map((link) =>
              link.type === "route" ? (
                <Link
                  key={link.label}
                  to={link.path}
                  onClick={() => setMenuOpen(false)}
                  className="text-2xl font-semibold text-stone-800 transition-colors duration-300 hover:text-[#C8762A]"
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.label}
                  type="button"
                  onClick={() => handleScroll(link.id)}
                  className="text-left text-2xl font-semibold text-stone-800 transition-colors duration-300 hover:text-[#C8762A]"
                >
                  {link.label}
                </button>
              )
            )}

            <Link
              to="/contact"
              onClick={() => setMenuOpen(false)}
              className="mt-4 rounded-xl bg-[#1A1714] py-4 text-center font-semibold text-white transition-colors duration-300 hover:bg-[#C8762A]"
            >
              Book Consultation
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}